import React, { useEffect, useMemo, useState } from 'react';
import NewBaseTable from 'components/NewBaseTable/NewBaseTable';
import { PartQuote, TwoWayPartQuote } from 'types';
import { generateTwoWayQuotesColumnConfigs } from './OrderUtility';
import { Pagination } from 'components';
import { ITEMS_PER_PAGE } from '.';
import { SipTableContainer } from 'common/pages/fleetV2/machine/aseptic/views/MachineHealth/components/SipTableWidget/SipTableWidget.elements';
import { useQuoteContextMenuState } from './useQuoteContextMenuState';
import OrderPdfModal from './OrderPdfModal';
import { saveAs } from 'file-saver';
import { TFunction } from 'i18next';
import styled from 'styled-components';

const TwoWayOrderHistoryTableContainer = styled(SipTableContainer)`
  &.sip-table-widget {
    .toolbar-wrapper {
      display: flex;
    }
  }
`;

interface Props {
  orderQuotes: PartQuote[];
  formatPODate: (date: string) => string;
  t: TFunction<["fpns"], undefined>;
  isDataFetching: boolean;
  showQuoteModal: (quote: PartQuote) => void;
  setCurrentPage: (page: number) => void;
  totalRecords: number;
  currentPage: number;
  handleDeleteQuote?: (quote: PartQuote) => void;
  setQuoteSearchKey?: (searchKey: string) => void;
  setQuoteFilter?: (filter: string) => void;
}
const defaultSortState = {
  id: 'orderNumber',
  desc: true
};

// Normalize optional status values so the quote filter can compare labels case-insensitively.
const normalizeStatus = (value?: string): string => value?.trim().toLowerCase() || '';

const addProductsToSubRows = (orderQuotes: PartQuote[]): TwoWayPartQuote[] => {
  return orderQuotes.map((order) => ({
    ...order,
    createDate: order.requestDate,
    expirationDate: order.expiryDate,
    quoteNumber: order.requestid,
    productCount: Array.isArray(order.parts) ? order.parts.length : 0,
    parts: Array.isArray(order.parts)
      ? order.parts.map((part) => ({
          ...part,
          id: part.partId as string,
          leadTime: part.avgleadtime ? Number(part.avgleadtime) : 0,
          stock: part.stock ? Number(part.stock) : 0,
          subtotal: part.subtotal 
        }))
      : [],
    subRows: Array.isArray(order.parts)
      ? [
          {
            newTable: order.parts.map((part) => ({
              id: part.partId as string,
              name: part.description,
              description: part.description,
              sku: part.sku,
              leadTime: part.leadTime ? part.leadTime === 'N/A' ? 0 : part.leadTime : 0,
              stock: part.stock,
              subtotal: part.subtotal,
              quantity: part.quantity,
              urgent: part.urgent,
              tentativeDeliveryDate: part.tentativeDeliveryDate,
              price: part.unitPrice as string,
              priceUnit: part.currency as string,
              orderStatus: order.status,
             
            })),
            type: 'twowayorder'
          }
        ]
      : []
  }));
};
const TwoWayOrderQuote = ({
  orderQuotes,
  formatPODate,
  t,
  isDataFetching,
  showQuoteModal,
  setCurrentPage,
  totalRecords,
  currentPage,
  handleDeleteQuote,
  setQuoteSearchKey,
  setQuoteFilter,
}: Props): JSX.Element => {
  const [showPagination, setShowPagination] = React.useState(true);
  const [hasActiveSearch, setHasActiveSearch] = useState(false);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('');
  const [showQuoteDetailsModal, setShowQuoteDetailsModal] = useState(false);
  const [linkToPdf, setLinkToPdf] = useState<string | undefined>(undefined);
  const [poNumber, setPoNumber] = useState<string | undefined>(undefined);
  const {
    contextMenuPosition,
    openContextMenu,
    closeContextMenu,
    selectedQuote,
    handleAddQuoteToCart
  } = useQuoteContextMenuState();

  const twoWayOrder = useMemo(
    () => orderQuotes && addProductsToSubRows(orderQuotes),
    [orderQuotes]
  );

  useEffect(() => {
    setShowPagination(!hasActiveSearch && selectedStatusFilter === '');
  }, [hasActiveSearch, selectedStatusFilter]);

  const updateSearchValue = (inputValue?: string) => {
    setHasActiveSearch((inputValue?.trim().length ?? 0) > 0);
    setQuoteSearchKey?.(inputValue ?? '');
  };

  const handleFilterRequest = (status: string) => {
    status = status === 'All' ? '' : status;
    setSelectedStatusFilter(status || '');
    setQuoteFilter?.(status || '');
  };

  const handleViewQuoteDetails = (quote: PartQuote) => {
    setLinkToPdf(quote.linkToPdfInBlobStorage);
    setPoNumber(quote.quoteNumber as string);
    setShowQuoteDetailsModal(true);
  };

  const downloadOrderDetails = () => {
    saveAs(linkToPdf as string, `order-quote-${poNumber}.pdf`);
  };

  return (
    <>
      <TwoWayOrderHistoryTableContainer className="widget-ui__main sip-table-widget">
        <NewBaseTable
          newTableData={twoWayOrder as TwoWayPartQuote[]}
          columnConfigs={generateTwoWayQuotesColumnConfigs({
            formatPODate,
            t,
            showQuoteModal,
            contextMenuPosition,
            openContextMenu,
            closeContextMenu,
            selectedQuote,
            handleAddQuoteToCart,
            handleViewQuoteDetails,
            handleDeleteQuote
          })}
          sortState={defaultSortState}
          enableFilter={true}
          isShowGlobalSearch={true}
          isShowColumnOptions={true}
          handleFilterRequest={handleFilterRequest}
          searchInputValue={updateSearchValue}


          enableFilterOptionsSearch={true}
          
          
          
          
          searchPlaceholder={"search parts"}
          customClassName="parts-table"
     

          isDataLoading={isDataFetching}
          tdMarginLeft="0.625rem"
          textAlign="left"
          hasNestedRows={true}
        />
      </TwoWayOrderHistoryTableContainer>
      {showPagination && twoWayOrder && twoWayOrder.length > 0 && (
        <Pagination
          onPageChange={(page) => setCurrentPage(page)}
          itemsPerPage={ITEMS_PER_PAGE}
          numItems={totalRecords}
          currentPage={currentPage}
        />
      )}
      {showQuoteDetailsModal && (
        <OrderPdfModal
          showOrderDetails={showQuoteDetailsModal}
          hideOrderModal={() => setShowQuoteDetailsModal(false)}
          downloadOrderDetails={downloadOrderDetails}
          t={t}
          fileUrl={linkToPdf}
          isQuote={true}
        />
      )}
    </>
  );
};

export default TwoWayOrderQuote;
